import * as React from 'react'
import FlexView from '@/modules/shared/components/FlexView'
import { useTypedParams } from 'react-router-typesafe-routes/dom'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { useActivity } from '@/modules/vehicles/stores/activityStore'
import useVehicleActivityService from '../../../services/ActivityService'
import { VehicleCalendarModel } from '@/app/models/vehicle/calendar/vehicleCalendarModel'
import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import VehicleYearlyActivityCalendar from '../components/charts/yearly/VehicleYearlyActivityCalendar'
import { VEHICLE_ROUTES } from '../../../routes-typed'

const Yearly = () => {
  // Typed URL params: { companyId: number, vehicleId: number, year: number }.
  const { companyId, vehicleId, year: currentYear } = useTypedParams(
    VEHICLE_ROUTES.calendar,
  )
  const { setYear, year } = useDate()
  const { setType } = useActivity()
  const { getVehicleCalendar } = useVehicleActivityService()
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { t } = useLocalize()

  const [calendarData, setCalendarData] =
    React.useState<VehicleCalendarModel | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)

  const fetchCalendar = async () => {
    const stopLoading = startLoading()
    setIsLoading(true)
    try {
      const data = await getVehicleCalendar({
        companyId,
        vehicleId,
        year: currentYear,
      })
      setCalendarData(data)
    } catch (error) {
      console.error('Error fetching vehicle calendar:', error)
    } finally {
      setIsLoading(false)
      stopLoading()
    }
  }

  React.useEffect(() => {
    const yearStr = String(currentYear)
    if (year !== yearStr) setYear(yearStr)
    fetchCalendar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear, vehicleId])

  React.useEffect(() => {
    setType('yearly')
  }, [])

  return (
    <div className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
      {isLoading && !calendarData ? (
        <div className="tw-grid tw-h-[500px] tw-w-full tw-place-items-center">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
            <LoaderIcon className="tw-h-16 tw-w-16 tw-animate-spin tw-text-primary" />
            <span className="tw-text-gray-600">
              {t('driverModule.loading-calendar')}
            </span>
          </div>
        </div>
      ) : calendarData ? (
        <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
          <VehicleYearlyActivityCalendar
            calendar={calendarData}
            companyId={String(companyId)}
            vehicleId={vehicleId}
            isLoading={isLoading}
            refreshCalendar={fetchCalendar}
          />
        </div>
      ) : (
        <div className="tw-grid tw-h-[500px] tw-flex-1 tw-place-items-center tw-p-4">
          <FlexView vAlignContent="center" hAlignContent="center">
            <span style={{ color: '#97A2B3' }} className="text-xl tw-my-4">
              {t('driverCalendar.no-data')}
            </span>
          </FlexView>
        </div>
      )}
    </div>
  )
}

export default Yearly
